import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation } from '@apollo/client';
import { Alert, CardContent } from '@guildeducationinc/recess';
import DeleteOutlineIcon from '@guildeducationinc/recess/icons/DeleteOutlineIcon';
import EditIcon from '@guildeducationinc/recess/icons/EditIcon';
import { AdminTopNav } from '@src/components/Admin/AdminTopNav/AdminTopNav';
import { useClientIdParam } from '@src/hooks/useClientIdParam';
import { Card } from 'antd';
import { pick } from 'lodash';
import { AUTH_CONFIGS, Confirm, DataTable, Form, Provider, SamlIdentifierField, SAVE_AUTH_CONFIG, TrackableButton, useAlert, useAuthConfigs, useLocale, } from 'pe-core-frontend';
import { useMemo, useState } from 'react';
import { useSystemClientId } from '@src/hooks/useSystemClientId';
export const AuthConfigs = ({ isSystem = false }) => {
    // get clientId based on whether this is a system-level page or a client-specific Auth page
    const { systemClientId } = useSystemClientId();
    const clientIdParam = useClientIdParam();
    const clientId = (isSystem ? systemClientId : clientIdParam) ?? 0;
    const isGuildClient = !isSystem && systemClientId === clientId;
    const { t } = useLocale();
    const { showAlert } = useAlert();
    const { items, loading, error, deleteAuthConfig } = useAuthConfigs({
        clientId,
        provider: isSystem ? Provider.GUILD_AUTH : undefined,
        excludeGuildAuth: !isSystem, // Show Guild Auth provider for the system-level page, not for the client-specific Auth pages
    });
    const [saveAuthConfig] = useMutation(SAVE_AUTH_CONFIG, {
        refetchQueries: [AUTH_CONFIGS],
    });
    const [selectedAuthConfig, setSelectedAuthConfig] = useState(null);
    const [configToDelete, setConfigToDelete] = useState(null);
    const providerOptions = useMemo(() => {
        // provider list:
        // - only GUILD_AUTH for system-level Authentication page
        // - everything except GUILD_AUTH for client pages
        // - GUILD_AUTH_CONSUMER is not allowed for the Guild system client
        //
        if (isSystem) {
            return [{ label: 'GUILD_AUTH', value: Provider.GUILD_AUTH }];
        }
        return Object.keys(Provider)
            .map(p => ({ label: p, value: p }))
            .filter(p => p.value !== Provider.GUILD_AUTH &&
            !(isGuildClient && p.value === Provider.GUILD_AUTH_CONSUMER));
    }, [isSystem, isGuildClient]);
    // TODO: need to dynamically disable/enable these fields based on Provider onChange, but Form doesn't support it.
    // We can try moving all the fields to the children of Form and handle the disabling there. For ex:
    //  - frontend/src/components/Admin/EditMacro/EditMacro.tsx
    //  - pe-local/frontend/src/components/Admin/EditPaymentRuleModal/EditPaymentRuleModal.tsx
    const FIELDS = useMemo(() => {
        let isDisabled = false;
        let isSyncDisabled = false;
        const isNew = selectedAuthConfig === undefined || selectedAuthConfig?.id === 0;
        if (!isNew) {
            isDisabled =
                selectedAuthConfig?.provider !== Provider.SAML &&
                    selectedAuthConfig?.provider !== Provider.GUILD_AUTH;
            isSyncDisabled = selectedAuthConfig?.provider !== Provider.GUILD_AUTH_CONSUMER;
        }
        return [
            {
                name: 'name',
                label: t('Config Name'),
                type: 'text',
            },
            {
                name: 'provider',
                label: t('Provider'),
                type: 'lookup',
                options: providerOptions,
            },
            {
                name: 'isActive',
                label: t('Active?'),
                type: 'boolean',
                // Guild Auth cannot be made inactive. Client-specific Auth configs can be toggled on/off at the client level.
                disabled: isSystem,
            },
            {
                name: 'enableProductAccessSync',
                label: t('Enable Product Access Sync?'),
                type: 'boolean',
                disabled: isSyncDisabled,
                infoText: t('Only applicable when using GUILD_AUTH_CONSUMER provider'),
            },
            {
                name: 'syncEmployeeProfiles',
                label: t('Sync Employee Profiles?'),
                type: 'boolean',
                disabled: isSyncDisabled,
                infoText: t('Only applicable when Product Access Sync is enabled'),
            },
            {
                name: 'config',
                label: t('Config'),
                type: 'textarea',
                plainText: true,
                helpText: t('JSON config for the provider'),
                disabled: isDisabled,
                infoText: t('Only applicable for SAML and GUILD_AUTH providers'),
            },
            {
                name: 'samlUseNameid',
                label: t('Use NameID?'),
                type: 'boolean',
                disabled: isDisabled,
                infoText: t('Only applicable for SAML and GUILD_AUTH providers. If false, SAML Attribute Name must be provided'),
            },
            {
                name: 'samlAttributeName',
                label: t('SAML Attribute Name'),
                type: 'text',
                disabled: isDisabled,
                infoText: t('Only applicable if not using NameID for SAML or GUILD_AUTH providers'),
            },
            {
                name: 'samlIdentifierField',
                label: t('Identifier Field'),
                type: 'lookup',
                options: Object.keys(SamlIdentifierField).map(p => ({ label: p, value: p })),
                disabled: isDisabled,
                infoText: t('Only applicable for SAML and GUILD_AUTH providers'),
            },
            ...(isSystem
                ? [
                    {
                        name: 'extraData',
                        label: t('Extra Data'),
                        type: 'textarea',
                        plainText: true,
                        helpText: t('Optional extra JSON data used to process the SAML response'),
                    },
                ]
                : []),
        ];
    }, [isSystem, providerOptions, selectedAuthConfig, t]);
    const getDuplicateProviderError = (config, items, provider) => {
        if (!items)
            return undefined;
        if (config.provider === provider &&
            items.some(item => item.provider === provider && item.id !== config.id)) {
            return t('Only one {provider} config is allowed', {
                values: { provider },
            });
        }
        return undefined;
    };
    const handleValidate = (config) => {
        const errors = {};
        if (!config.name) {
            errors.name = t('Name is required');
        }
        // only allow saving one DIRECT and one GUILD_AUTH_CONSUMER config per client.
        // only allow saving one GUILD_AUTH config in the whole system.
        // the only client we allow to manage the GUILD_AUTH config is the 'guild' system client, so this
        // validation will work without any special extra conditions.
        const providerError = getDuplicateProviderError(config, items, Provider.DIRECT) ||
            getDuplicateProviderError(config, items, Provider.GUILD_AUTH_CONSUMER) ||
            getDuplicateProviderError(config, items, Provider.GUILD_AUTH);
        if (!config.provider) {
            errors.provider = t('Provider is required');
        }
        else if (isGuildClient && config.provider === Provider.GUILD_AUTH_CONSUMER) {
            // guild system client cannot have guild_auth_consumer
            errors.provider = t('Guild system client cannot have GUILD_AUTH_CONSUMER provider');
        }
        else if (providerError) {
            errors.provider = providerError;
        }
        if (config.provider === Provider.SAML) {
            if (!config.config) {
                errors.config = t('Configuration JSON is required for SAML');
            }
            if (!config.samlIdentifierField) {
                errors.samlIdentifierField = t('Identifier Field is required for SAML');
            }
            if (String(config.samlUseNameid) === 'false' && !config.samlAttributeName) {
                errors.samlAttributeName = t('SAML Attribute Name is required when not using NameID');
            }
        }
        return errors;
    };
    const handleEditClick = (itemId) => {
        const fullItem = items?.find(item => item.id === itemId);
        setSelectedAuthConfig(fullItem || null);
    };
    const handleSave = async (config) => {
        try {
            // extraData will be a string. Convert to object
            let extraDataObj = {};
            if (config.extraData && typeof config.extraData === 'string') {
                try {
                    extraDataObj = JSON.parse(config.extraData);
                }
                catch { }
            }
            else {
                showAlert(t('Invalid data in extraData. Expected string.'), 'error');
            }
            if (config.provider === Provider.GUILD_AUTH_CONSUMER) {
                // config.enableProductAccessSync will be a string. save it as a boolean in extraData
                const productAccessSyncEnabled = String(config.enableProductAccessSync) === 'true';
                extraDataObj.product_access_sync_enabled = productAccessSyncEnabled;
                // config.syncEmployeeProfiles will be a string. save it as a boolean in extraData
                // But only enable it if product access sync is also enabled
                extraDataObj.sync_employee_profiles =
                    productAccessSyncEnabled && String(config.syncEmployeeProfiles) === 'true';
            }
            await saveAuthConfig({
                variables: {
                    input: {
                        ...pick(config, ['id', 'name', 'provider']),
                        config: config.config || {},
                        // Guild Auth must be always active; client-specific Auth configs can be toggled on/off at the client level
                        isActive: isSystem ? true : config.isActive.toString() === 'true',
                        samlUseNameid: config.samlUseNameid.toString() === 'true',
                        samlAttributeName: config.samlAttributeName || null,
                        samlIdentifierField: config.samlIdentifierField || null,
                        extraData: extraDataObj,
                        clientId,
                    },
                },
            });
            showAlert(t('Auth config saved'));
            setSelectedAuthConfig(null);
        }
        catch (e) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            showAlert(e.message, 'error');
        }
    };
    const handleDelete = async () => {
        if (configToDelete) {
            try {
                await deleteAuthConfig({
                    variables: {
                        id: configToDelete.id,
                    },
                });
                showAlert(t('Auth config deleted'));
                setConfigToDelete(null);
            }
            catch (e) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                showAlert(e.message, 'error');
            }
        }
    };
    const getFormExtraData = (selectedAuthConfig) => {
        return selectedAuthConfig?.extraData
            ? JSON.stringify(selectedAuthConfig.extraData, null, 2)
            : {};
    };
    const getFormEnableProductAccessSync = (selectedAuthConfig) => {
        return selectedAuthConfig?.extraData?.product_access_sync_enabled === true;
    };
    const getFormSyncEmployeeProfiles = (selectedAuthConfig) => {
        return selectedAuthConfig?.extraData?.sync_employee_profiles === true;
    };
    const getProductAccessSyncColumn = () => {
        // product access sync column should not be shown for Guild Auth system page.
        // this is only applicable to GUILD_AUTH_CONSUMER, so don't show a value for other providers.
        return isSystem
            ? []
            : [
                {
                    field: 'enableProductAccessSync',
                    headerName: t('Product Access Sync?'),
                    valueGetter: (e) => {
                        if (e.row.provider === Provider.GUILD_AUTH_CONSUMER) {
                            return e.row.extraData?.product_access_sync_enabled ? t('Yes') : t('No');
                        }
                        return '-';
                    },
                    flex: 0.2,
                    sortable: false,
                },
            ];
    };
    return (_jsxs("div", { children: [_jsx(AdminTopNav, { title: t('Authentication'), actions: !isSystem && (_jsx(TrackableButton, { btnType: 'mui', enText: 'Add Auth Config', tag: 'header', variant: 'contained', onClick: () => setSelectedAuthConfig({
                        id: 0,
                        clientId,
                        name: '',
                        provider: '',
                        isActive: true,
                        enableProductAccessSync: false,
                        syncEmployeeProfiles: false,
                        config: {},
                        samlUseNameid: false,
                        samlAttributeName: '',
                        samlIdentifierField: '',
                    }), children: t('Add Auth Config') })) }), _jsx("div", { children: _jsx(Card, { children: _jsxs(CardContent, { children: [error && (_jsx(Alert, { display: true, hideClose: true, type: 'danger', children: error.message })), _jsx(DataTable, { loading: loading, disableRowSelectionOnClick: true, disableColumnMenu: true, rows: items || [], hideFooter: true, columns: [
                                    {
                                        field: 'name',
                                        headerName: t('Config Name'),
                                        flex: 0.2,
                                        sortable: false,
                                    },
                                    {
                                        field: 'provider',
                                        headerName: t('Provider'),
                                        flex: 0.2,
                                        sortable: false,
                                    },
                                    {
                                        field: 'isActive',
                                        headerName: t('Active?'),
                                        valueGetter: e => (e.row.isActive ? 'Yes' : 'No'),
                                        flex: 0.2,
                                        sortable: false,
                                    },
                                    // conditionally show the Product Access Sync column when the page is not in system mode
                                    ...getProductAccessSyncColumn(),
                                    {
                                        field: 'action',
                                        headerName: t('Actions'),
                                        renderCell: ({ row }) => (_jsxs("div", { children: [_jsx(TrackableButton, { btnType: 'icon', enText: 'Edit', tag: `auth_config_${row.id}`, label: t('Edit'), appearance: 'minimal', icon: _jsx(EditIcon, { size: 20 }), onClick: () => handleEditClick(row.id), className: 'margin--right_8' }), !isSystem && (_jsx(TrackableButton, { btnType: 'icon', enText: 'Delete', tag: `auth_config_${row.id}`, label: t('Delete'), appearance: 'minimal', icon: _jsx(DeleteOutlineIcon, { size: 20 }), onClick: () => setConfigToDelete(row) }))] })),
                                        width: 100,
                                        sortable: false,
                                    },
                                ] }), selectedAuthConfig && (_jsx(Form, { title: selectedAuthConfig.id ? 'Edit Auth Config' : 'Add Auth Config', modal: true, obj: {
                                    ...selectedAuthConfig,
                                    config: JSON.stringify(selectedAuthConfig.config, null, 2),
                                    extraData: getFormExtraData(selectedAuthConfig),
                                    enableProductAccessSync: getFormEnableProductAccessSync(selectedAuthConfig),
                                    syncEmployeeProfiles: getFormSyncEmployeeProfiles(selectedAuthConfig),
                                }, fields: FIELDS, onValidate: handleValidate, onSave: handleSave, onCancel: () => setSelectedAuthConfig(null) })), configToDelete && (_jsx(Confirm, { message: t('Are you sure you want to delete this auth config?'), onConfirm: handleDelete, onCancel: () => setConfigToDelete(null) }))] }) }) })] }));
};
